import { computed, ref, watch } from 'vue';
import { useRoute } from 'vue-router';
import { createSharedComposable } from '@vueuse/core';
import { useUpc } from '@nuxt-common/media-card-large/app/composables/useUpc';
import { usePageData } from '~/composables/usePageData';
import useKillswitches from '~/modules/featureControl/composables/useKillswitches';
import useMarxentCta from '~/modules/marxent/composables/useMarxentCta';
import { useBrand } from '~/composables/useBrand';
import { useDeviceType } from '~/composables/utils/useDeviceType';
import useExperiments from '~/stores/useExperimentsStore';
import { useEnvProps } from '~/composables/useEnvProps';
import useMarxent from '~/modules/marxent/composables/useMarxent';
import { OSVersionSupported } from '~/utils/OSVersionSupportedUtil';
import useFeatureEligibility from '~/modules/featureControl/composables/useFeatureEligibility';
import useSelectedMemberId from '~/modules/latchkey/composables/useSelectedMemberId';
import { useTopLevelCategoryFlags } from '~/modules/featureControl/composables/useTopLevelCategoryFlags';

export const useMarxentIndicator = createSharedComposable(() => {
  const { productId } = usePageData();
  const selectedMemberId = useSelectedMemberId();
  const { isShopTheSetExpEnabled } = useFeatureEligibility();
  const { selectedUpcNumber } = useUpc(isShopTheSetExpEnabled ? selectedMemberId : productId);
  const { killswitches } = useKillswitches();
  const { autoStartMarxentExperience } = useMarxentCta();
  const { isDesktop } = useDeviceType();
  const {
    experimentFlags: { isImageIndicatorStylingEnabled, isImageARIndicatorEnabled },
  } = useExperiments();
  const { marxentApiCredentials } = useEnvProps();
  const { initMarxent, setQrCode } = useMarxent();
  const { isMcom } = useBrand();
  const { isHomeCategory, isSeasonalCelebrationsCategory } = useTopLevelCategoryFlags();

  const isArReady = computed(() => isMcom && OSVersionSupported() && (isHomeCategory || isSeasonalCelebrationsCategory));
  const isArEligible = computed(() => !!marxentValues.value?.isArAssetsAvailable);

  const isMarxentEligible = ref(false);
  const marxentValues = ref(null);

  /** @type {import('vue').ComputedRef<string | null>} */
  const marxentUrl = computed(() => {
    if (isMarxentEligible.value && !isDesktop.value) {
      return marxentValues.value?.marxentUrl || null;
    }
    return null;
  });

  const isMarxentImageIndicator = computed(() => {
    return !!(isMarxentEligible.value && isImageIndicatorStylingEnabled && isImageARIndicatorEnabled);
  });

  const marxentUrlForMobile = computed(() => {
    if (isDesktop.value) return null;
    return marxentUrl.value || null;
  });

  /**
   * Fetches the Marxent URL and updates the marxentUrl ref.
   * @param {number} upcNumber
   */
  const checkForMarxentAssetsAndUrl = async (upcNumber) => {
    if (isArReady.value) {
      try {
        const { marxentUrl, isArAssetsAvailable } = await initMarxent(upcNumber, marxentApiCredentials);
        marxentValues.value = { marxentUrl, isArAssetsAvailable };
      } catch (e) {
        console.error(e);
      }
    }
  };

  const webArCheck = async () => {
    if (useRoute()?.name === 'writeReview' || !killswitches.isBigTicketWebAREnabled) {
      isMarxentEligible.value = false;
      return;
    }

    await checkForMarxentAssetsAndUrl(selectedUpcNumber.value);

    if (!!setQrCode && isArEligible.value && isImageIndicatorStylingEnabled && isImageARIndicatorEnabled) {
      if (!isDesktop.value) {
        autoStartMarxentExperience();
      }

      isMarxentEligible.value = true;
    } else {
      isMarxentEligible.value = false;
    }
  };

  watch([selectedUpcNumber, isArEligible], webArCheck, { immediate: true });

  return {
    isMarxentEligible,
    isMarxentImageIndicator,
    marxentUrl,
    marxentUrlForMobile,
  };
});
